import { inject, injectable } from 'inversify';

import { BOOKINGKIT_API_FACTORY, SUPPLIER_STATE_SERVICE } from '@/bootstrap/ServiceProviders';
import { BookingKitApiFactoryInterface } from '@/models/Factories/BookingKitApiFactoryInterface';
import {
    PaymentProvider, PaymentProviderSwitchRequest,
    WalletBalanceType,
    WalletTransaction,
} from '@/services/AccountingService/types';
import {
    mapTransactionToWalletTransaction,
    fromApiPaymentMethodToPaymentMethod,
    fromApiVendorPaymentMethodToVendorPaymentMethod,
    fromApiWalletToApplicationWallet,
    fromApiPaymentProviderToApplicationPaymentProvider,
} from '@/services/AccountingService/Reducers';

import { AccountingServiceError }
    from '@/services/AccountingService/Errors/AccountingServiceError';
import { UseSupplierInterface } from '@/state/supplier';
import {
    PaymentMethodType
} from '@/modules/sell/pages/PaymentMethods/types';
import {
    PaymentMethod
} from '@bookingkit/api-v4/dist/models';
import type { AdditionalFees } from '@bookingkit/api-v4/models/additional-fees';
import { SetActivationIntentTypeEnum } from '@bookingkit/api-v4/models/set-activation-intent';
import type { UpdatePaymentMethodRequest } from '@bookingkit/api-v4/models/update-payment-method-request';
import type { SetPaymentMethodsRequestInner } from '@bookingkit/api-v4/models/set-payment-methods-request-inner';
import { AccountingServiceWalletError }
    from '@/services/AccountingService/Errors/AccountingServiceWalletError';
import {
    ListPayoutDestinationResponse,
} from '@bookingkit/api-v4/models/list-payout-destination-response';
import { Money } from '@bookingkit/api-v4';
import {
    VendorPaymentMethodType
} from "@/modules/admin/Pages/VendorPaymentMethods/Types/VendorPaymentMethod";

type UpdatePaymentMethodRequestWithNullFees = Omit<UpdatePaymentMethodRequest, 'additional_fees'> & {
    additional_fees?: AdditionalFees | null;
};

@injectable()
export class AccountingService {
    private connection: ReturnType<BookingKitApiFactoryInterface['getConnection']>;

    private supplierId: string;

    constructor(
    @inject(BOOKINGKIT_API_FACTORY) factory: BookingKitApiFactoryInterface,
    @inject(SUPPLIER_STATE_SERVICE) useSupplierState: () => UseSupplierInterface,
    ) {
        const { supplier } = useSupplierState();
        if (!supplier.value?.id) {
            throw AccountingServiceError.fromMissingSupplier();
        }
        this.supplierId = supplier.value.id;
        this.connection = factory.getConnection();
        this.getPaymentProviders = this.getPaymentProviders.bind(this);
    }

    async createPayoutDestination(walletId: string, country: string, currency: string, accountNumber: string):
        Promise<PaginatedResponse<WalletTransaction>> {
        const { supplierId } = this;

        await this.connection
            .payoutDestinationCreate({
                vendorId: supplierId,
                walletId: walletId,
                createPayoutDestinationRequest: {
                    country,
                    currency,
                    account_number: accountNumber,
                    type: 'BANK_ACCOUNT',
                }
            });

        return {
            data: [],
        };
    }

    async deletePayoutDestination(walletId: string, payoutDestinationId: string):
      Promise<ListPayoutDestinationResponse> {
        const { supplierId } = this;
        await this.connection
            .payoutDestinationDelete({
                vendorId: supplierId,
                walletId: walletId,
                payoutDestinationId: payoutDestinationId
            });

        return {
            data: [],
        };
    }

    async listPayoutDestinations(walletId: string):
        Promise<ListPayoutDestinationResponse> {
        const { supplierId } = this;

        const { data: response } = await this.connection
            .payoutDestinationList({
                vendorId: supplierId,
                walletId
            });

        return response;
    }

    async getAllTransactions(walletId: string, args?: PaginatedRequest<{types?: string[]}>):
    Promise<PaginatedResponse<WalletTransaction>> {
        const { supplierId } = this;
        const parameters = args ?? {};
        const {
            types, from, to, pageToken,
        } = parameters;

        const { data: response } = await this.connection
            .listTransactions({
                vendorId: supplierId,
                walletId,
                types: types,
                from,
                to,
                pageToken: pageToken ?? undefined
            });

        const { data, metadata } = response;
        if (!data) {
            throw AccountingServiceError
                .fromMissingProperty('data');
        }
        if (!metadata) {
            throw AccountingServiceError
                .fromMissingProperty('metadata');
        }
        const transactions = data
            .map(mapTransactionToWalletTransaction);
        return {
            data: transactions,
            nextPageToken: metadata["page-token"] ?? undefined,
        };
    }

    async getAllWallets(): Promise<WalletBalanceType[]> {
        const { supplierId } = this;
        const { data } = await this.connection.walletsV2({
            vendorId: supplierId
        });
        if (!data.data) {
            throw AccountingServiceError.fromMissingProperty('data');
        }
        if (data.data.length === 0) {
            throw AccountingServiceWalletError.fromMissingWallet(supplierId);
        }
        return data.data?.map(fromApiWalletToApplicationWallet);
    }

    async getPaymentProviders(): Promise<PaymentProvider[]> {
        const { supplierId } = this;
        const { data } = await this.connection.paymentProviders({
            vendorId: supplierId
        });
        if (!data.data) {
            throw AccountingServiceError.fromMissingProperty('paymentProviders');
        }
        return data.data.map(fromApiPaymentProviderToApplicationPaymentProvider);
    }

    async setSwitchToPaymentProvider(paymentProviderSwitchRequest: PaymentProviderSwitchRequest):
    Promise<PaymentProvider> {
        const { supplierId } = this;
        const {
            providerId, type, date,
        } = paymentProviderSwitchRequest;
        const data = await this
            .connection
            .switchPaymentProviders({
                vendorId: supplierId,
                providerId,
                setActivationIntent: { type: type as SetActivationIntentTypeEnum, date }
            });
        if (!data?.data) {
            throw AccountingServiceError.fromMissingProperty('paymentProviders');
        }
        return fromApiPaymentProviderToApplicationPaymentProvider(data.data);
    }

    async getPaymentMethods(): Promise<PaymentMethodType[]> {
        const { supplierId } = this;

        const { data } = await this.connection.getPaymentMethods({
            vendorId: supplierId
        });
        if (!data?.data) {
            throw new Error('Response was malformed');
        }
        return data.data?.map(fromApiPaymentMethodToPaymentMethod);
    }

    async getVendorPaymentMethods(): Promise<VendorPaymentMethodType[]> {
        const { supplierId } = this;

        const { data } = await this.connection.getVendorPaymentMethods({
            vendorId: supplierId
        });
        if (!data?.data) {
            throw new Error('Response was malformed');
        }
        return data.data?.map(fromApiVendorPaymentMethodToVendorPaymentMethod);
    }

    async updateVendorPaymentMethod(paymentMethodId: string, data: Partial<VendorPaymentMethodType>): Promise<VendorPaymentMethodType> {
        const { supplierId } = this;

        const { data: responseData } = await this.connection.updateVendorPaymentMethod({
            vendorId: supplierId,
            paymentMethodId,
            updateVendorPaymentMethodRequest: data
        });
        if (!responseData) {
            throw new Error('Response was malformed');
        }
        return fromApiVendorPaymentMethodToVendorPaymentMethod(responseData);
    }


    async setPaymentMethods(methods: PaymentMethod[]): Promise<void> {
        const { supplierId } = this;
        const { data } = await this.connection.setPaymentMethods({
            vendorId: supplierId,
            setPaymentMethodsRequestInner: methods as SetPaymentMethodsRequestInner[]
        });
        if (!data) {
            throw AccountingServiceError.fromMissingRequestProperty('data', 'setPaymentMethods');
        }
    }

    /**
     * The response is the updated method itself (CheckoutPaymentMethodResponse is the bare
     * schema, not the list envelope the GET uses), and it is the only place the provider's
     * pending_verification for a just-written method can be read.
     */
    async setCheckoutPaymentMethod(paymentMethodId: string, data: UpdatePaymentMethodRequestWithNullFees): Promise<PaymentMethodType> {
        const { supplierId } = this;
        const { data: responseData } = await this.connection.updatePaymentMethod({
            vendorId: supplierId,
            paymentMethodId,
            updatePaymentMethodRequest: data as UpdatePaymentMethodRequest,
        });
        if (!responseData) {
            throw new Error('Response was malformed');
        }
        return fromApiPaymentMethodToPaymentMethod(responseData);
    }

    async requestPayoutV2(walletId: string, payoutDestinationId: string, amount: Money): Promise<void> {
        const { supplierId } = this;
        await this.connection
            .payoutCreateV2({
                vendorId: supplierId,
                walletId,
                createPayoutRequest: { amount, destination_id: payoutDestinationId }
            });
    }
}

export default { AccountingService };
